import { useMemo, useState } from 'react';
import {
  InShellPanel,
  InShellPanelDescription,
  InShellPanelHeader,
  InShellPanelTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { useTroubleshootSessions } from '../hooks/useTroubleshoot';
import { SessionsTable } from '../components/SessionsTable';
import { TroubleshootTranscript } from '../components/TroubleshootTranscript';
import { Stethoscope } from 'lucide-react';

/** Cross-tenant troubleshoot session list (console tier). Read-only — a row opens the transcript in a
 *  right-side slide-over (stays on the list; deep-link / device-drawer use the full-page route instead). */
export function TroubleshootSessionsPage() {
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('all');
  const [status, setStatus] = useState('all');
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  const { data, loading, error } = useTroubleshootSessions();
  const sessions = data?.staffTroubleshootSessions ?? [];

  // Client-side filtering over the returned window (mirrors Activity Log).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sessions.filter((s) => {
      if (mode !== 'all' && s.mode !== mode) return false;
      if (status !== 'all' && s.status !== status) return false;
      if (!q) return true;
      return [s.title, s.deviceName, s.deviceUUID, s.actor, s.accountId]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [sessions, search, mode, status]);

  return (
    <div className="flex flex-col gap-6 p-6" data-testid="troubleshoot-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <Stethoscope className="h-6 w-6" />
          {'Troubleshooting Sessions'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {'All troubleshooting sessions across every account. Read-only.'}
        </p>
      </div>

      {/* Filter bar — client-side over the loaded window. */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">{'Search'}</label>
          <Input
            data-testid="ts-filter-search"
            placeholder="title / device / UUID / actor / account"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-72"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">{'Mode'}</label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="w-32" data-testid="ts-filter-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{'All'}</SelectItem>
              <SelectItem value="staff">{'staff'}</SelectItem>
              <SelectItem value="customer">{'customer'}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">{'Status'}</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-36" data-testid="ts-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{'All'}</SelectItem>
              <SelectItem value="running">{'running'}</SelectItem>
              <SelectItem value="ended">{'ended'}</SelectItem>
              <SelectItem value="failed">{'failed'}</SelectItem>
              <SelectItem value="creating">{'creating'}</SelectItem>
              <SelectItem value="paused">{'paused'}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error.message}
        </div>
      ) : loading && !sessions.length ? (
        <div className="text-sm text-muted-foreground">{'Loading sessions…'}</div>
      ) : (
        <SessionsTable
          sessions={filtered}
          onRowClick={(s) => setSelectedSessionId(s.sessionId)}
          emptyText="No troubleshooting sessions match."
        />
      )}

      {/* Slide-over transcript — opens in place so you keep the list + filters behind it. */}
      <InShellPanel
        open={!!selectedSessionId}
        onOpenChange={(next) => !next && setSelectedSessionId(null)}
        side="right"
        data-testid="troubleshoot-session-panel"
        closeLabel={'Close'}
      >
        {/* Fill the panel + scroll only the transcript body (header/close stay fixed) — scio3 parity. */}
        <div className="flex h-full min-h-0 flex-col">
          <InShellPanelHeader className="mb-4 shrink-0">
            <InShellPanelTitle data-testid="troubleshoot-session-panel-title">
              {'Troubleshooting session'}
            </InShellPanelTitle>
            <InShellPanelDescription className="sr-only">
              {'Read-only session transcript'}
            </InShellPanelDescription>
          </InShellPanelHeader>
          <div className="-mr-6 min-h-0 flex-1 overflow-y-auto pr-6 scroll-thin">
            {selectedSessionId ? (
              <TroubleshootTranscript
                sessionId={selectedSessionId}
                session={sessions.find((s) => s.sessionId === selectedSessionId)}
              />
            ) : null}
          </div>
        </div>
      </InShellPanel>
    </div>
  );
}
